import { useEffect } from 'react'
import { DateUtil, TIME } from '@utils/dateUtil'
import { weekdays } from '@utils/weekdays'
import { RangePattern, RecurrenceT, TypePattern } from '@providers'
import { createProperDate } from '@utils'

type useRecurrenceT = {
  fromDate: DateUtil
  recurrence?: RecurrenceT
  timezone: string
  maxDate: DateUtil
  setRecurredBooking: (param: Partial<RecurrenceT>, fromDate: DateUtil) => void
}

const useBookingRecurrence = ({
  fromDate,
  recurrence,
  timezone,
  setRecurredBooking,
  maxDate,
}: useRecurrenceT) => {
  useEffect(() => {
    if (recurrence && !recurrence?.dateLimit.isSame(maxDate, TIME.DAYS))
      setRecurredBooking({ dateLimit: maxDate }, fromDate)
  }, [fromDate, maxDate, recurrence, setRecurredBooking])

  if (!recurrence || recurrence.typePattern === TypePattern.NEVER) return []

  const dailyCondition = (
    next: DateUtil,
    limit: DateUtil,
    recurrence: RecurrenceT,
    count: number
  ) => {
    return (
      next.isSameOrBefore(limit, TIME.DAYS) &&
      (recurrence.rangePattern === RangePattern.ON_DATE
        ? next.isSameOrBefore(recurrence.rangeDate, TIME.DAYS)
        : count < recurrence.rangeInterval)
    )
  }

  const weeklyCondition = (
    nextWeek: DateUtil,
    limit: DateUtil,
    recurrence: RecurrenceT,
    count: number
  ) => {
    return (
      nextWeek.isSameOrBefore(limit, TIME.WEEKS) &&
      (recurrence.rangePattern === RangePattern.ON_DATE
        ? nextWeek.isSameOrBefore(recurrence.rangeDate, TIME.WEEKS)
        : count < recurrence.rangeInterval)
    )
  }

  const monthlyCondition = (
    next: DateUtil,
    limit: DateUtil,
    recurrence: RecurrenceT,
    count: number
  ) => {
    return (
      next.isSameOrBefore(limit, TIME.WEEKS) &&
      (recurrence.rangePattern === RangePattern.ON_DATE
        ? next.isSameOrBefore(recurrence.rangeDate, TIME.DAYS)
        : count < recurrence.rangeInterval)
    )
  }

  const createRecurrencies = () => {
    const result: DateUtil[] = []
    const limit = recurrence.dateLimit.endOf(TIME.DAYS)
    let count = 0
    const isEndDateRecurrence = recurrence.rangePattern === RangePattern.ON_DATE

    if (fromDate.isBefore(DateUtil.tz(timezone), TIME.DAYS)) return result
    if (!recurrence.typeInterval) return result

    if (recurrence.typePattern === TypePattern.DAILY) {
      const next = fromDate.clone()
      while (dailyCondition(next, limit, recurrence, count)) {
        result.push(createProperDate(next))
        next.add(recurrence.typeInterval, TIME.DAYS)
        count += 1
      }
    }

    if (recurrence.typePattern === TypePattern.WEEKLY) {
      if (!recurrence.daysOfWeek.length) return result

      let nextWeek = fromDate.clone().startOf(TIME.WEEKS)
      while (weeklyCondition(nextWeek, limit, recurrence, count)) {
        let nextDay = nextWeek.clone()
        for (let i = 0; i < 7; i++) {
          const day = weekdays[nextDay.day()]
          if (
            isEndDateRecurrence
              ? nextDay.isAfter(recurrence.rangeDate, TIME.DAYS)
              : count >= recurrence.rangeInterval
          ) {
            break
          }

          if (
            recurrence.daysOfWeek.includes(day) &&
            nextDay.isSameOrAfter(fromDate, TIME.DAYS) &&
            nextDay.isSameOrBefore(limit, TIME.DAYS)
          ) {
            result.push(createProperDate(nextDay))
            count++
          }
          nextDay = nextDay.clone().add(1, TIME.DAYS)
        }
        nextWeek = nextWeek.add(recurrence.typeInterval, TIME.WEEKS)
      }
    }

    if (recurrence.typePattern === TypePattern.MONTHLY) {
      let next = fromDate.clone()

      while (monthlyCondition(next, limit, recurrence, count)) {
        if (next.isSameOrAfter(fromDate, TIME.DAYS) && next.isSameOrBefore(limit, TIME.DAYS)) {
          result.push(createProperDate(next))
          count++
        }
        next = next.add(recurrence.typeInterval, TIME.MONTHS)
      }
    }
    return result
  }

  //   const updateBookingDeskDatesRanges = ({ allDayToggle, start, end, to }) => {
  //     setBookingDeskDateChange({ startDate, endDate })

  //     let fromDate
  //     let toDate
  //     if (allDayToggle || !hourlyBookingEnabled) {
  //       ;({ startDate: fromDate, endDate: toDate } = calculateBookingLocalDate(from, to, timezone, {
  //         bookingWorkdayTimeFrom: hcBookingWorkDayTimeFrom,
  //         bookingWorkdayTimeTo: hcBookingWorkDayTimeTo,
  //       }))
  //     } else {
  //       ;({ startDate: fromDate, endDate: toDate } = getDateForHourly({
  //         start,
  //         end,
  //         to,
  //       }))
  //     }
  //     let error = fromDate.isSameOrAfter(toDate)
  //     if (isBookingDeskPage || toDate.diff(fromDate, 'hours') > 23)
  //       // diff used here for checking when using admin selector
  //       checkHourly(fromDate, toDate).then((canBook) => {
  //         if (!error && !canBook) error = canBook // if no error, but cannot book, setError
  //       })
  //     props.updateBookingDeskDate(
  //       error
  //         ? { error }
  //         : {
  //             allDayToggle,
  //             fromDate: fromDate.set({ second: 0, millisecond: 0 }),
  //             toDate: toDate.set({ second: 0, millisecond: 0 }),
  //             error,
  //           }
  //     )
  //   }

  //   const createHighlighted = (someArrayOfBookings: any[]) =>
  //     Array.from(
  //       someArrayOfBookings.reduce((dates, booking) => {
  //         const { startDate: start, endDate: end } = booking
  //         const nextDateTz = parseDate(start, timezone).startOf(TIME.DAYS)
  //         const endDateTz = parseDate(end, timezone).endOf(TIME.DAYS)

  //         while (nextDateTz.isBefore(endDateTz)) {
  //           // DateUtil has a toDate() function that will convert the momentDate to the current user's timezone
  //           // toDate() will ignore the timezone of the DateUtil object so set manually instead of relying
  //           // on toDate()
  //           const momentToDate = createProperDate(nextDateTz)
  //           dates.add(momentToDate)
  //           nextDateTz.add(1, TIME.DAYS)
  //         }
  //         return dates
  //       }, new Set())
  //     )

  const value = createRecurrencies()
  return value.length === 0 ? [fromDate.clone()] : value
}

export default useBookingRecurrence
